/**
 * What has been captured against one thing under test, and what is left.
 *
 * Pure: no React, no storage, no network. The screens render this and the queue
 * persists it, but the rules about what counts as answered, what a verdict is,
 * and what is still outstanding live here where they can be tested without a
 * browser — and, more importantly, where the report generator can use exactly
 * the same rules later.
 *
 * **Scope is the load-bearing idea.** A reading belongs to one breaker, not to
 * the board it sits in. Without the breaker in the key, 192 breakers on MY02
 * would share one set of answers and the first one tested would appear complete
 * for all of them.
 */

import type { CapturedShot, ShotProfile, TemplateItem, TemplateSection, Verdict } from './types.js';
import { evaluate, rollup, type ResultInput, type SectionRollup } from './verdict.js';
import { shotProgress, type ShotProgress } from './progress.js';

/** Identifies what a capture belongs to: a board, or one breaker on it. */
export interface Scope {
  assetId: number;
  breakerRef?: string;
}

export const scopeKey = (s: Scope): string =>
  s.breakerRef ? `${s.assetId}/${s.breakerRef}` : String(s.assetId);

export const answerKey = (s: Scope, itemId: number): string => `${scopeKey(s)}:${itemId}`;

/** Everything captured so far, across every scope in the project. */
export interface CaptureState {
  answers: Record<string, ResultInput>;
  /** Photos keyed by scope, then by shot code. */
  shots: Record<string, Record<string, CapturedShot>>;
}

export const emptyCapture = (): CaptureState => ({ answers: {}, shots: {} });

export function answerOf(state: CaptureState, scope: Scope, itemId: number): ResultInput {
  return state.answers[answerKey(scope, itemId)] ?? {};
}

/** Records an answer, returning new state. Never mutates — the UI diffs on identity. */
export function record(state: CaptureState, scope: Scope, itemId: number, input: ResultInput): CaptureState {
  return { ...state, answers: { ...state.answers, [answerKey(scope, itemId)]: input } };
}

export function recordShot(state: CaptureState, scope: Scope, shot: CapturedShot): CaptureState {
  const key = scopeKey(scope);
  return { ...state, shots: { ...state.shots, [key]: { ...(state.shots[key] ?? {}), [shot.shotCode]: shot } } };
}

export function shotsFor(state: CaptureState, scope: Scope): CapturedShot[] {
  return Object.values(state.shots[scopeKey(scope)] ?? {});
}

/**
 * Which checks belong to a breaker rather than to the board.
 *
 * Phase rotation, earth loop, breaker operation and meter verification are taken
 * at each way; everything else is a property of the board. Asking a board-level
 * check once per breaker would multiply it by 192 on MY02.
 */
export const BREAKER_SECTION_NAMES: readonly string[] = [
  'Breaker manual operation',
  'Phase Rotation',
  'Earth Loop Impedance',
  'Power Meter Reading Test',
];

export function isBreakerSection(section: TemplateSection): boolean {
  return BREAKER_SECTION_NAMES.some((n) => section.name.toLowerCase().includes(n.toLowerCase()));
}

export function sectionsFor(sections: readonly TemplateSection[], scope: Scope): TemplateSection[] {
  const wantBreaker = scope.breakerRef !== undefined;
  return sections.filter((s) => isBreakerSection(s) === wantBreaker).sort((a, b) => a.seq - b.seq);
}

export interface CheckView {
  item: TemplateItem;
  input: ResultInput;
  verdict: Verdict;
  reason: string;
  /** The acceptance band as it should read on screen and in the report. */
  band: string;
}

export interface SectionView {
  section: TemplateSection;
  checks: CheckView[];
  rollup: SectionRollup;
}

export interface ScopeProgress {
  sections: SectionView[];
  /** Across every check in scope. */
  overall: SectionRollup;
  shots?: ShotProgress;
}

/**
 * Builds everything a capture screen needs for one scope.
 *
 * Verdicts are computed here, never read from anything stored — a stored verdict
 * and a moved acceptance limit disagree silently, and the report would print the
 * stale one.
 */
export function viewScope(
  state: CaptureState,
  scope: Scope,
  sections: readonly TemplateSection[],
  items: readonly TemplateItem[],
  opts?: { profiles?: readonly ShotProfile[]; breakerType?: string; describeBand?: (i: TemplateItem) => string },
): ScopeProgress {
  const describe = opts?.describeBand;
  const inScope = sectionsFor(sections, scope);
  const sectionViews: SectionView[] = inScope.map((section) => {
    const checks: CheckView[] = items
      .filter((i) => i.sectionId === section.id)
      .sort((a, b) => a.seq - b.seq)
      .map((item) => {
        const input = answerOf(state, scope, item.id);
        const result = evaluate(item, input);
        return {
          item,
          input,
          verdict: result.verdict,
          reason: result.reason,
          band: describe ? describe(item) : (item.acceptanceCriteria ?? ''),
        };
      });
    return { section, checks, rollup: rollup(checks.map((c) => c.verdict)) };
  });

  const all = sectionViews.flatMap((s) => s.checks.map((c) => c.verdict));
  const out: ScopeProgress = { sections: sectionViews, overall: rollup(all) };

  if (opts?.profiles && opts.breakerType) {
    const captured = Object.values(state.shots[scopeKey(scope)] ?? {});
    out.shots = shotProgress(opts.profiles, opts.breakerType as never, captured);
  }
  return out;
}

/** One line for a lineup list: is this breaker done, and how far along? */
export interface BreakerSummary {
  ref: string;
  verdict: Verdict;
  shotsCaptured: number;
  shotsRequired: number;
  complete: boolean;
}

export function summariseBreaker(
  state: CaptureState,
  scope: Scope,
  sections: readonly TemplateSection[],
  items: readonly TemplateItem[],
  profiles: readonly ShotProfile[],
  breakerType: string,
): BreakerSummary {
  const view = viewScope(state, scope, sections, items, { profiles, breakerType });
  const shots = view.shots;
  return {
    ref: scope.breakerRef ?? '',
    verdict: view.overall.overall,
    shotsCaptured: shots?.captured ?? 0,
    shotsRequired: shots?.required ?? 0,
    // Complete means both: every check resolved AND every required photo taken.
    // A breaker with all its readings and none of its photos is not finished,
    // and a screen that says otherwise is how evidence goes missing.
    complete: view.overall.overall !== 'Pending' && (shots ? shots.complete : true),
  };
}
